import React, { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Layout } from "../components/Layout";
import { UserCard } from "../components/UserCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Trophy, Medal, Star } from "lucide-react";

const LeaderboardPage = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${API}/stats/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 20 }
        });
        setUsers(response.data);
      } catch (error) {
        console.error("Leaderboard error:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchLeaderboard();
    }
  }, [token]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="animate-fadeIn">
          <div className="flex items-center gap-3 mb-1">
            <Trophy size={32} className="text-amber-500" />
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Classement
            </h1>
          </div>
          <p className="text-slate-500">
            Les utilisateurs les mieux notés
          </p>
        </div>

        {/* Podium for top 3 */}
        {!loading && users.length >= 3 && (
          <div className="grid grid-cols-3 gap-3 animate-fadeIn stagger-1">
            {/* Second Place */}
            <Card className="guardian-card mt-6">
              <CardContent className="p-4 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mx-auto mb-2">
                  <Medal size={24} className="text-slate-600" />
                </div>
                <p className="font-bold text-sm truncate">{users[1]?.full_name}</p>
                <p className="text-xl font-extrabold text-[#FF3366]" style={{ fontFamily: 'Outfit' }}>
                  {users[1]?.average_rating.toFixed(1)}
                </p>
                <p className="text-xs text-slate-500">{users[1]?.total_ratings} avis</p>
              </CardContent>
            </Card>

            {/* First Place */}
            <Card className="guardian-card border-2 border-amber-400 animate-pulse-glow">
              <CardContent className="p-4 text-center">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
                  <Trophy size={28} className="text-amber-500" />
                </div>
                <p className="font-bold truncate">{users[0]?.full_name}</p>
                <p className="text-2xl font-extrabold text-[#FF3366]" style={{ fontFamily: 'Outfit' }}>
                  {users[0]?.average_rating.toFixed(1)}
                </p>
                <p className="text-xs text-slate-500">{users[0]?.total_ratings} avis</p>
              </CardContent>
            </Card>

            {/* Third Place */}
            <Card className="guardian-card mt-10">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-2">
                  <Medal size={20} className="text-amber-600" />
                </div>
                <p className="font-bold text-sm truncate">{users[2]?.full_name}</p>
                <p className="text-lg font-extrabold text-[#FF3366]" style={{ fontFamily: 'Outfit' }}>
                  {users[2]?.average_rating.toFixed(1)}
                </p>
                <p className="text-xs text-slate-500">{users[2]?.total_ratings} avis</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full List */}
        <Card className="guardian-card animate-fadeIn stagger-2" data-testid="leaderboard-list">
          <CardHeader>
            <CardTitle className="font-extrabold" style={{ fontFamily: 'Outfit' }}>
              Classement complet
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-2xl" />
                ))}
              </div>
            ) : users.length > 0 ? (
              <div className="space-y-3">
                {users.map((user, index) => (
                  <UserCard key={user.id} user={user} rank={index + 1} />
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <Star size={40} className="empty-state-icon" />
                <p className="font-medium">Pas encore de données</p>
                <p className="text-sm mt-1">Le classement apparaîtra bientôt</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LeaderboardPage;
